/**
 * Barra de filtros de la tabla de productos del backoffice (ADM-02).
 *
 * Todos se combinan entre si y con el buscador. Los de si/no son un `<select>`
 * de tres opciones y no una casilla: "sin foto" es tan util como "con foto"
 * (lo que falta completar), y una casilla solo sabe decir una de las dos. El
 * control que esta filtrando se pinta en azul (`.activo`), para que se vea de
 * un vistazo que la tabla no esta mostrando todo.
 */
import { useState } from 'react';

import type { OpcionesFiltroProductos, OrdenProductosAdmin } from '@bandu/compartido';

import { Icono } from '../comunes/Icono';
import { conCambio, ORDENES_PRODUCTOS } from '../../utilidades/filtros-productos';
import type { FiltrosProductos } from '../../utilidades/filtros-productos';
import { FiltroStock } from './FiltroStock';
import { SelectorCategorias } from './SelectorCategorias';
import { SelectorMarcas } from './SelectorMarcas';

export interface FiltrosProductosProps {
  filtros: FiltrosProductos;
  /** Nulo mientras cargan: los botones de categoria y marca esperan. */
  opciones: OpcionesFiltroProductos | null;
  onCambiar(filtros: FiltrosProductos): void;
}

type SiNo = 'todos' | 'si' | 'no';

function siNoDe(valor: boolean | undefined): SiNo {
  if (valor === undefined) return 'todos';
  return valor ? 'si' : 'no';
}

function booleanoDe(valor: SiNo): boolean | undefined {
  return valor === 'todos' ? undefined : valor === 'si';
}

function claseSelector(activo: boolean): string {
  return activo ? 'selector activo' : 'selector';
}

interface FiltroSiNoProps {
  id: string;
  etiqueta: string;
  valor: boolean | undefined;
  textos: { todos: string; si: string; no: string };
  onCambiar(valor: boolean | undefined): void;
}

function FiltroSiNo({ id, etiqueta, valor, textos, onCambiar }: FiltroSiNoProps) {
  return (
    <div className="filtro">
      <label className="filtro-et" htmlFor={id}>
        {etiqueta}
      </label>
      <select
        id={id}
        className={claseSelector(valor !== undefined)}
        value={siNoDe(valor)}
        onChange={(evento) => {
          onCambiar(booleanoDe(evento.target.value as SiNo));
        }}
      >
        <option value="todos">{textos.todos}</option>
        <option value="si">{textos.si}</option>
        <option value="no">{textos.no}</option>
      </select>
    </div>
  );
}

export function FiltrosProductos({ filtros, opciones, onCambiar }: FiltrosProductosProps) {
  const [selector, setSelector] = useState<'categorias' | 'marcas' | null>(null);

  const cambiar = (cambio: Partial<FiltrosProductos>) => {
    onCambiar(conCambio(filtros, cambio));
  };

  const cantidadCategorias = (filtros.lineas?.length ?? 0) + (filtros.categorias?.length ?? 0);
  const cantidadMarcas = filtros.marcas?.length ?? 0;

  return (
    <section className="filtros-productos" aria-label="Filtros de productos">
      <div className="filtro">
        <label className="filtro-et" htmlFor="filtro-visibilidad">
          Estado
        </label>
        <select
          id="filtro-visibilidad"
          className={claseSelector(filtros.visibilidad !== undefined)}
          value={filtros.visibilidad ?? 'todos'}
          onChange={(evento) => {
            const valor = evento.target.value;
            cambiar({
              visibilidad: valor === 'publicado' || valor === 'oculto' ? valor : undefined,
            });
          }}
        >
          <option value="todos">Todos</option>
          <option value="publicado">Publicados</option>
          <option value="oculto">Ocultos</option>
        </select>
      </div>

      <FiltroSiNo
        id="filtro-foto"
        etiqueta="Foto"
        valor={filtros.conFoto}
        textos={{ todos: 'Todas', si: 'Con foto', no: 'Sin foto' }}
        onCambiar={(conFoto) => {
          cambiar({ conFoto });
        }}
      />

      <FiltroSiNo
        id="filtro-precio"
        etiqueta="Precio"
        valor={filtros.conPrecio}
        textos={{ todos: 'Todos', si: 'Con precio', no: 'Sin precio' }}
        onCambiar={(conPrecio) => {
          cambiar({ conPrecio });
        }}
      />

      <FiltroStock filtros={filtros} onCambiar={cambiar} />

      <div className="filtro">
        <span className="filtro-et" id="filtro-categoria-et">
          Categoria
        </span>
        <button
          type="button"
          className={cantidadCategorias > 0 ? 'btn-filtro activo' : 'btn-filtro'}
          aria-labelledby="filtro-categoria-et filtro-categoria-valor"
          disabled={opciones === null}
          onClick={() => {
            setSelector('categorias');
          }}
        >
          <span id="filtro-categoria-valor">
            {cantidadCategorias > 0 ? `${cantidadCategorias} elegidas` : 'Todas'}
          </span>
          <Icono nombre="chevron" className="gira-abajo" />
        </button>
      </div>

      <div className="filtro">
        <span className="filtro-et" id="filtro-marca-et">
          Marca
        </span>
        <button
          type="button"
          className={cantidadMarcas > 0 ? 'btn-filtro activo' : 'btn-filtro'}
          aria-labelledby="filtro-marca-et filtro-marca-valor"
          disabled={opciones === null}
          onClick={() => {
            setSelector('marcas');
          }}
        >
          <span id="filtro-marca-valor">
            {cantidadMarcas > 0 ? `${cantidadMarcas} elegidas` : 'Todas'}
          </span>
          <Icono nombre="chevron" className="gira-abajo" />
        </button>
      </div>

      <label className="opcion-filtro filtro-casilla">
        <input
          type="checkbox"
          checked={filtros.soloDestacados === true}
          onChange={(evento) => {
            cambiar({ soloDestacados: evento.target.checked ? true : undefined });
          }}
        />
        Solo destacados
      </label>

      <div className="filtro filtro-orden">
        <label className="filtro-et" htmlFor="filtro-orden">
          Ordenar por
        </label>
        <select
          id="filtro-orden"
          className="selector"
          value={filtros.orden ?? 'nombre'}
          onChange={(evento) => {
            const orden = evento.target.value as OrdenProductosAdmin;
            cambiar({ orden: orden === 'nombre' ? undefined : orden });
          }}
        >
          {ORDENES_PRODUCTOS.map((opcion) => (
            <option key={opcion.valor} value={opcion.valor}>
              {opcion.texto}
            </option>
          ))}
        </select>
      </div>

      {selector === 'categorias' && opciones !== null ? (
        <SelectorCategorias
          lineas={opciones.lineas}
          lineasElegidas={filtros.lineas ?? []}
          categoriasElegidas={filtros.categorias ?? []}
          onCerrar={() => {
            setSelector(null);
          }}
          onAplicar={(lineas, categorias) => {
            setSelector(null);
            cambiar({ lineas, categorias });
          }}
        />
      ) : null}

      {selector === 'marcas' && opciones !== null ? (
        <SelectorMarcas
          marcas={opciones.marcas}
          elegidas={filtros.marcas ?? []}
          onCerrar={() => {
            setSelector(null);
          }}
          onAplicar={(marcas) => {
            setSelector(null);
            cambiar({ marcas });
          }}
        />
      ) : null}
    </section>
  );
}
